import { useState, useEffect } from "react";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/faq-section";
import { PAGE_FAQS } from "@/data/faqs";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";

const base = import.meta.env.BASE_URL.replace(/\/$/, "");

const DISHES = [
  { src: "/photos/dishes/pasta_seafood.webp",      alt: "Паста с морепродуктами в чернилах кальмара — авторское блюдо ресторана Паруса на крыше" },
  { src: "/photos/dishes/duck_confit.webp",         alt: "Утиное конфи с тыквенным пюре — горячее блюдо ресторана Паруса на крыше Петербург" },
  { src: "/photos/dishes/scallops_cauliflower.webp",alt: "Гребешки с цветной капустой и икрой — морские деликатесы в ресторане Паруса на крыше" },
  { src: "/photos/dishes/fish_cream.webp",           alt: "Белая рыба в сливочном соусе с икрой — фирменное блюдо Паруса на крыше" },
  { src: "/photos/dishes/pizza_truffle.webp",        alt: "Пицца с чёрным трюфелем — авторская пицца ресторана на крыше в Петербурге" },
  { src: "/photos/dishes/salad_burrata.webp",        alt: "Салат с буррата и апельсином — лёгкое блюдо Паруса на крыше СПб" },
  { src: "/photos/dishes/mussels.webp",              alt: "Запечённые мидии на камнях — закуска из морепродуктов ресторана Паруса" },
  { src: "/photos/dishes/carpaccio.webp",            alt: "Карпаччо из говядины с руколой и пармезаном — холодная закуска Паруса" },
  { src: "/photos/dishes/pizza_prosciutto.webp",     alt: "Неаполитанская пицца с прошутто и руколой — пицца из дровяной печи Паруса" },
  { src: "/photos/dishes/caesar_shrimp.webp",        alt: "Цезарь с тигровыми креветками — классический салат ресторана Паруса на крыше" },
  { src: "/photos/dishes/salad_green.webp",          alt: "Зелёный салат с овощами и зеленью — лёгкий сезонный салат Паруса" },
  { src: "/photos/dishes/salad_beef.webp",           alt: "Салат с говяжьим стейком и помидорами — сытный салат ресторана Паруса" },
  { src: "/photos/dishes/dessert_strawberry.webp",   alt: "Десерт с клубникой и меренгой в ракушке — авторский десерт Паруса на крыше" },
];

const PDF_TABS = [
  { id: "main",   label: "Основное меню",   file: `${base}/menu/menu_main.pdf`,  desc: "Авторская кухня · Завтраки · Закуски · Горячее · Хоспер · Десерты" },
  { id: "lunch",  label: "Бизнес-ланч",     file: `${base}/menu/menu_lunch.pdf`, desc: "Пн – Пт · 12:00 – 16:00 · от 590 ₽" },
  { id: "tea",    label: "Чайная карта",    file: `${base}/api/storage/public-objects/menus/tea-2026-c9f4716a0cb6.pdf`, desc: "Авторские чаи · Горячие и холодные" },
  { id: "wine",   label: "Винная карта",    file: `${base}/menu/menu_wine.pdf`,  desc: "Вина · Игристые · Крепкие напитки" },
  { id: "kids",   label: "Детское",         file: `${base}/api/storage/public-objects/menus/kids-2026-10-26.pdf`, desc: "Меню для наших маленьких гостей" },
];

export default function Menu() {
  const [activeTab, setActiveTab] = useState(PDF_TABS[0].id);
  const [loaded, setLoaded] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);

  // Fallback: if iframe onLoad never fires (Safari, some desktop browsers),
  // show the PDF area after 4 s so the spinner doesn't spin forever.
  useEffect(() => {
    setPdfLoaded(false);
    const timer = setTimeout(() => setPdfLoaded(true), 4000);
    return () => clearTimeout(timer);
  }, [activeTab]);

  useSeo({
    title: "Меню — Паруса на крыше | Ресторан на крыше в Петербурге",
    description: "Меню ресторана Паруса на крыше: авторская европейская кухня, бизнес-ланч, чайная и винная карты, детское меню. Сезонные продукты. Ул. Марата, 5.",
    keywords: "меню ресторан на крыше Петербург, авторская кухня СПб, бизнес-ланч Петербург, винная карта ресторан, Паруса на крыше меню",
    image: "/photos/food.jpg",
    canonical: "/menu",
  });
  useBreadcrumbs([{ name: "Меню", path: "/menu" }]);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 80);
  }, []);

  const active = PDF_TABS.find(t => t.id === activeTab)!;

  return (
    <div className="w-full min-h-screen bg-background relative z-10 pt-[100px] md:pt-[120px] pb-24 overflow-x-hidden">
      <div className="container mx-auto px-6 md:px-14 max-w-7xl relative">

        <div className="hidden sm:block absolute top-0 right-0 font-serif text-[130px] md:text-[240px] font-light leading-none text-primary/5 select-none pointer-events-none tracking-tighter -mt-20">Меню</div>

        <div className="mb-12 md:mb-16 relative z-10" style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}>
          <h1 className="text-4xl md:text-7xl font-serif font-light text-primary mb-6 tracking-wide">Карта блюд</h1>
          <div className="w-24 h-[1px] bg-primary/30" />
          <p className="mt-6 text-sm md:text-base text-foreground/60">
            Блюда <span className="text-primary font-medium">от 290 ₽</span>
            <span className="mx-2 text-foreground/25">·</span>
            средний чек <span className="text-primary font-medium">≈ 1300 ₽</span>
            <span className="mx-2 text-foreground/25">·</span>
            бизнес-ланч <span className="text-primary font-medium">от 590 ₽</span>
          </p>
          <button
            type="button"
            onClick={() => {
              if (activeTab !== "kids") {
                setActiveTab("kids");
                setPdfLoaded(false);
              }
              requestAnimationFrame(() => {
                document.getElementById("kids-menu-tab")?.scrollIntoView({
                  behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
                  block: "start",
                  inline: "center",
                });
              });
            }}
            className="mt-6 inline-flex items-center gap-3 border border-primary/45 px-5 py-3 text-xs uppercase tracking-[0.16em] text-primary hover:bg-primary hover:text-primary-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-colors"
          >
            Детское меню <span aria-hidden="true">→</span>
          </button>
        </div>

        {/* ── DISH PHOTO GALLERY ───────────────────────────────────────────── */}
        <div className="mb-14 relative z-10" style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 0.5s ease 0.08s, transform 0.5s ease 0.08s",
        }}>
          <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-6">Авторская кухня</p>
          <div
            className="flex gap-3 overflow-x-auto pb-3 md:grid md:grid-cols-4 lg:grid-cols-5 md:overflow-visible"
            style={{ scrollbarWidth: "none" }}
          >
            {DISHES.map((dish, i) => (
              <div
                key={i}
                className="shrink-0 w-[200px] md:w-auto overflow-hidden aspect-square"
              >
                <img
                  src={dish.src}
                  alt={dish.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 mb-10 border-b border-primary/20" style={{
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.5s ease 0.1s",
        }}>
          <div className="flex overflow-x-auto no-scrollbar" style={{ WebkitOverflowScrolling: "touch" }}>
            {PDF_TABS.map((tab) => (
              <button
                key={tab.id}
                id={tab.id === "kids" ? "kids-menu-tab" : undefined}
                onClick={() => { setActiveTab(tab.id); setPdfLoaded(false); }}
                className={`px-5 py-4 text-[10px] tracking-[0.25em] uppercase transition-all relative border-b-2 -mb-[2px] shrink-0 whitespace-nowrap scroll-mt-28 ${
                  activeTab === tab.id
                    ? "text-accent border-accent"
                    : "text-muted-foreground border-transparent hover:text-primary hover:border-primary/30"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="relative z-10" style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 0.5s ease 0.15s, transform 0.5s ease 0.15s",
        }}>
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <p className="text-xs text-muted-foreground/70 tracking-wider font-light">{active.desc}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <a
                href={active.file}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-[9px] tracking-[0.3em] uppercase text-muted-foreground/70 border border-primary/20 px-5 py-2.5 hover:text-primary hover:border-primary/50 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
                Открыть в новой вкладке
              </a>
              <a
                href={active.file}
                download
                className="flex items-center gap-2 text-[9px] tracking-[0.3em] uppercase text-accent border border-primary/30 px-5 py-2.5 hover:bg-primary/10 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Скачать PDF
              </a>
            </div>
          </div>

          <div className="relative bg-card/30 border border-primary/15" style={{ minHeight: "80vh" }}>
            {!pdfLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10">
                <div className="w-8 h-8 border border-primary/30 border-t-accent rounded-full animate-spin" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground/50">Загрузка меню…</span>
              </div>
            )}
            <iframe
              key={activeTab}
              src={active.file}
              className="w-full border-0"
              style={{
                height: "82vh",
                minHeight: "600px",
                opacity: pdfLoaded ? 1 : 0,
                transition: "opacity 0.4s ease",
              }}
              title={active.label}
              onLoad={() => setPdfLoaded(true)}
            />
          </div>

          <div className="mt-6 p-4 border border-primary/10 bg-card/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-[10px] text-muted-foreground/50 tracking-wider">
              Если PDF не открывается — нажмите «Открыть в новой вкладке» или «Скачать PDF»
            </p>
            <button
              onClick={openBookingModal}
              className="bg-transparent border border-primary/45 text-accent px-8 py-3 text-[9px] tracking-[0.3em] uppercase hover:bg-primary hover:text-primary-foreground transition-all whitespace-nowrap"
            >
              Забронировать столик
            </button>
          </div>
        </div>
      </div>

      <FaqSection items={PAGE_FAQS["/menu"]} title="Вопросы о меню" />
    </div>
  );
}
